import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3,
  Users,
  FolderKanban,
  ClipboardList,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { useMetadata } from '@/context/MetadataContext';
import { getAccessibleProjects } from '@/services/projects.service';
import { getMyWorkPackages, getStatusIdFromLink, getProjectIdFromLink } from '@/services/workpackages.service';
import { Button, Card, LoadingState } from '@/components/common';
import type { Project, WorkPackage } from '@/types';

const CHART_COLORS = {
  todo: '#F59E0B',
  inProgress: '#3B82F6',
  completed: '#10B981',
  cancelled: '#EF4444',
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'];

export function CentralDashboardPage() {
  const { user } = useAuth();
  const { statusGroups } = useMetadata();

  const [projects, setProjects] = useState<Project[]>([]);
  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [projectsData, wpData] = await Promise.all([
        getAccessibleProjects(),
        getMyWorkPackages({}),
      ]);

      setProjects(projectsData);
      setWorkPackages(wpData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Eroare la încărcarea datelor');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Compute statistics
  const stats = useMemo(() => {
    const todoStatusIds = new Set(statusGroups.todo.map((s) => s.id));
    const inProgressStatusIds = new Set(statusGroups.inProgress.map((s) => s.id));
    const completedStatusIds = new Set(statusGroups.completed.map((s) => s.id));
    const cancelledStatusIds = new Set(statusGroups.cancelled.map((s) => s.id));

    let todo = 0;
    let inProgress = 0;
    let completed = 0;
    let cancelled = 0;

    workPackages.forEach((wp) => {
      const statusId = getStatusIdFromLink(wp);
      if (!statusId) return;

      if (cancelledStatusIds.has(statusId)) {
        cancelled++;
      } else if (completedStatusIds.has(statusId)) {
        completed++;
      } else if (inProgressStatusIds.has(statusId)) {
        inProgress++;
      } else if (todoStatusIds.has(statusId)) {
        todo++;
      }
    });

    return {
      totalProjects: projects.length,
      totalWorkPackages: workPackages.length,
      todo,
      inProgress,
      completed,
      cancelled,
      completionRate: workPackages.length > 0
        ? Math.round((completed / workPackages.length) * 100)
        : 0,
    };
  }, [projects, workPackages, statusGroups]);

  // Pie chart data - by status
  const statusPieData = useMemo(() => [
    { name: 'De făcut', value: stats.todo, color: CHART_COLORS.todo },
    { name: 'În lucru', value: stats.inProgress, color: CHART_COLORS.inProgress },
    { name: 'Finalizate', value: stats.completed, color: CHART_COLORS.completed },
    { name: 'Respinse', value: stats.cancelled, color: CHART_COLORS.cancelled },
  ].filter(d => d.value > 0), [stats]);

  // Bar chart data - by project
  const projectBarData = useMemo(() => {
    const todoStatusIds = new Set(statusGroups.todo.map((s) => s.id));
    const inProgressStatusIds = new Set(statusGroups.inProgress.map((s) => s.id));
    const completedStatusIds = new Set(statusGroups.completed.map((s) => s.id));
    const cancelledStatusIds = new Set(statusGroups.cancelled.map((s) => s.id));

    const projectCounts = new Map<number, { name: string; todo: number; inProgress: number; completed: number; cancelled: number; total: number }>();

    workPackages.forEach(wp => {
      const projectId = getProjectIdFromLink(wp);
      if (!projectId) return;

      const projectName = wp._links.project?.title || `Proiect ${projectId}`;
      const statusId = getStatusIdFromLink(wp);

      if (!projectCounts.has(projectId)) {
        projectCounts.set(projectId, { name: projectName, todo: 0, inProgress: 0, completed: 0, cancelled: 0, total: 0 });
      }

      const counts = projectCounts.get(projectId)!;
      counts.total++;
      if (statusId && cancelledStatusIds.has(statusId)) {
        counts.cancelled++;
      } else if (statusId && completedStatusIds.has(statusId)) {
        counts.completed++;
      } else if (statusId && inProgressStatusIds.has(statusId)) {
        counts.inProgress++;
      } else if (statusId && todoStatusIds.has(statusId)) {
        counts.todo++;
      }
    });

    return Array.from(projectCounts.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [workPackages, statusGroups]);

  // Projects pie chart
  const projectsPieData = useMemo(() => {
    return projectBarData.slice(0, 6).map((p, i) => ({
      name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name,
      value: p.total,
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [projectBarData]);

  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login
    : '';

  if (isLoading) {
    return <LoadingState message="Se încarcă panoul central..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary-600" />
            Panou central
          </h1>
          <p className="text-gray-600 mt-1">
            Bine ați venit, {userName}! Aceasta este o privire de ansamblu asupra activității dvs.
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={loadData}
        >
          Reîmprospătare
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <StatCard
          icon={<FolderKanban className="w-6 h-6" />}
          label="Proiecte"
          value={stats.totalProjects}
          color="bg-blue-500"
        />
        <StatCard
          icon={<ClipboardList className="w-6 h-6" />}
          label="Total activități"
          value={stats.totalWorkPackages}
          color="bg-gray-500"
        />
        <StatCard
          icon={<Clock className="w-6 h-6" />}
          label="De făcut"
          value={stats.todo}
          color="bg-amber-500"
        />
        <StatCard
          icon={<TrendingUp className="w-6 h-6" />}
          label="În lucru"
          value={stats.inProgress}
          color="bg-blue-500"
        />
        <StatCard
          icon={<CheckCircle className="w-6 h-6" />}
          label="Finalizate"
          value={stats.completed}
          color="bg-green-500"
        />
        <StatCard
          icon={<XCircle className="w-6 h-6" />}
          label="Respinse"
          value={stats.cancelled}
          color="bg-red-500"
        />
        <StatCard
          icon={<Users className="w-6 h-6" />}
          label="Rata finalizare"
          value={`${stats.completionRate}%`}
          color="bg-purple-500"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Status Distribution */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuție activități după status</h3>
          <div className="h-[300px]">
            {statusPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {statusPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Nu există date de afișat
              </div>
            )}
          </div>
        </Card>

        {/* Pie Chart - By Project */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuție activități per proiect</h3>
          <div className="h-[300px]">
            {projectsPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectsPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {projectsPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Nu există date de afișat
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6">
        {/* Bar Chart - By Project with status breakdown */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Activități per proiect (detaliat)</h3>
          <div className="h-[400px]">
            {projectBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={projectBarData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={200}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="todo" stackId="a" fill={CHART_COLORS.todo} name="De făcut" />
                  <Bar dataKey="inProgress" stackId="a" fill={CHART_COLORS.inProgress} name="În lucru" />
                  <Bar dataKey="completed" stackId="a" fill={CHART_COLORS.completed} name="Finalizate" />
                  <Bar dataKey="cancelled" stackId="a" fill={CHART_COLORS.cancelled} name="Respinse" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Nu există date de afișat
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Projects Summary Table */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sumar proiecte</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Proiect</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Total</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">De făcut</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">În lucru</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Finalizate</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Respinse</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Progres</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projectBarData.map((project, index) => {
                const progress = project.total > 0
                  ? Math.round((project.completed / project.total) * 100)
                  : 0;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{project.name}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{project.total}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {project.todo}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {project.inProgress}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {project.completed}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {project.cancelled}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 w-10 text-right">{progress}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {projectBarData.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    Nu există proiecte de afișat
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Stats card component
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color} text-white`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}
